import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAds } from '../../hooks/useAds';
import { useTelegramContext } from '../../context/TelegramContext';
import { enqueueStatusChange, enqueueDelete } from '../../db/sync-queue';
import { photoUrl } from '../../services/api-base';
import type { AdStatus } from '@barter/shared';
import { EmptyState } from '../../components/EmptyState/EmptyState';

export function MyAdsPage() {
  const navigate = useNavigate();
  const { user } = useTelegramContext();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<AdStatus | 'all'>('active');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [bulkLoading, setBulkLoading] = useState(false);

  const TABS: { value: AdStatus | 'all'; label: string }[] = [
    { value: 'active', label: t('myAds.active') },
    { value: 'sold', label: t('myAds.sold') },
    { value: 'archived', label: t('myAds.archive') },
    { value: 'all', label: t('myAds.all') },
  ];

  const sellerId = user?.id;
  const status = activeTab === 'all' ? undefined : activeTab;

  const { items, loading, error, refresh } = useAds(
    sellerId != null ? { sellerId, status } : undefined,
  );

  if (!user) {
    return <EmptyState icon="🔒" text={t('myAds.openInTelegram')} />;
  }

  const handleStatusChange = async (adId: string, newStatus: AdStatus) => {
    setActionLoading(adId);
    try {
      await enqueueStatusChange(adId, newStatus);
      refresh();
    } catch {
      alert(t('myAds.error'));
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (adId: string) => {
    if (!window.confirm(t('myAds.confirmDelete'))) return;
    setActionLoading(adId);
    try {
      await enqueueDelete(adId);
      refresh();
    } catch {
      alert(t('myAds.deleteError'));
    } finally {
      setActionLoading(null);
    }
  };

  const handleBulkStatus = async (status: AdStatus) => {
    if (!window.confirm(t('myAds.bulkConfirm'))) return;
    setBulkLoading(true);
    try {
      // Массовая смена — просто цепочка обычных операций (права проверит publisher)
      for (const ad of items) {
        await enqueueStatusChange(ad.id, status);
      }
      refresh();
    } catch {
      alert(t('myAds.bulkError'));
    } finally {
      setBulkLoading(false);
    }
  };

  return (
    <div>
      <h2 className="section-title">{t('myAds.title')}</h2>

      <div className="tabs">
        {TABS.map((tab) => (
          <button
            key={tab.value}
            className={`tabs__item${activeTab === tab.value ? ' tabs__item--active' : ''}`}
            onClick={() => setActiveTab(tab.value)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {error && <div className="error-state">{error}</div>}
      {loading && <div className="loading-state">{t('common.loading')}</div>}

      {!loading && items.length > 0 && (
        <div className="my-ads__bulk">
          <button
            className="btn btn--small"
            onClick={() => handleBulkStatus('archived')}
            disabled={bulkLoading}
          >
            {t('myAds.archiveAll')}
          </button>
          <button
            className="btn btn--small"
            onClick={() => handleBulkStatus('sold')}
            disabled={bulkLoading}
          >
            {t('myAds.soldAll')}
          </button>
        </div>
      )}

      {!loading && items.length === 0 && (
        <EmptyState icon="📋" text={t('myAds.noAds')} hint={t('myAds.createFirst')} />
      )}

      <div className="offer-list">
        {items.map((ad) => (
          <div key={ad.id} className="my-offer-card">
            <button
              className="my-offer-card__content"
              onClick={() => navigate(`/ad/${ad.id}`)}
              type="button"
            >
              <div className="my-offer-card__photo">
                {ad.photos[0] ? (
                  <img src={photoUrl(ad.photos[0])} alt={ad.title} loading="lazy" />
                ) : (
                  <span>📷</span>
                )}
              </div>
              <div className="my-offer-card__info">
                <div className="my-offer-card__title">{ad.title}</div>
                <div className={`status-badge status-badge--${ad.status}`}>
                  {ad.status === 'active'
                    ? t('adDetails.active')
                    : ad.status === 'sold'
                      ? t('adDetails.sold')
                      : t('adDetails.archived')}
                </div>
              </div>
            </button>
            <div className="my-offer-card__actions">
              {ad.status !== 'active' && (
                <button
                  className="btn btn--small"
                  onClick={() => handleStatusChange(ad.id, 'active')}
                  disabled={actionLoading === ad.id}
                >
                  {t('myAds.activate')}
                </button>
              )}
              {ad.status !== 'sold' && (
                <button
                  className="btn btn--small"
                  onClick={() => handleStatusChange(ad.id, 'sold')}
                  disabled={actionLoading === ad.id}
                >
                  {t('myAds.markSold')}
                </button>
              )}
              {ad.status !== 'archived' && (
                <button
                  className="btn btn--small"
                  onClick={() => handleStatusChange(ad.id, 'archived')}
                  disabled={actionLoading === ad.id}
                >
                  {t('myAds.toArchive')}
                </button>
              )}
              <button
                className="btn btn--small btn--danger"
                onClick={() => handleDelete(ad.id)}
                disabled={actionLoading === ad.id}
              >
                {t('myAds.delete')}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
